// Shared navigation model for the sidebar implementations.
//
// The nav item definitions, permission requirements and route-matching helpers
// live here so `Sidebar` (legacy) and `SidebarV2` (DQ-8763 AutoXLR8 Theme-2
// redesign) render the same navigation from a single source of truth.

import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import {
  IconFileProcessingSvg,
  IconLinkedServicesSvg,
  IconReportsWhiteSvg,
  IconResearchQueryHistorySvg,
  IconUserSvg,
  IconScoringSvg,
  IconRunInstanceSvg,
} from "../common/utils/icons";

export interface NavSubItem {
  text: string;
  url: string;
  icon: JSX.Element;
  routes?: string[];
  requiredPermission?: { entity: string; action: string };
  requiredAnyPermission?: { entity: string; action: string }[];
}
export interface NavItem {
  text: string;
  url?: string;
  icon?: JSX.Element;
  isStroke?: boolean;
  routes?: string[];
  children?: NavSubItem[];
  requiredPermission?: { entity: string; action: string };
  requiredAnyPermission?: { entity: string; action: string }[];
}

export const navItems: NavItem[] = [
  {
    text: "Configurations",
    icon: <IconFileProcessingSvg />,
    isStroke: true,
    children: [
      {
        text: "Domain",
        url: "domain",
        icon: <StorageOutlinedIcon />,
        routes: [
          "/",
          "/domain",
          "/domain/add",
          "/domain/view/:id",
          "/domain/edit/:id",
          "/domain/auditDomain/:id",
          "/domain/import-entity",
        ],
        requiredPermission: { entity: "domain", action: "view" },
      },
      {
        text: "Domain Linkage",
        url: "/domain-linkage",
        icon: <AccountTreeOutlinedIcon />,
        routes: [
          "/domain-linkage",
          "/domain-linkage/add",
          "/domain-linkage/view/:id",
          "/domain-linkage/edit/:id",
        ],
        requiredPermission: { entity: "domain_linkage", action: "view" },
      },
      {
        text: "Resource",
        url: "resource/all",
        icon: <StorageOutlinedIcon />,
        routes: [
          "/resource/:domainId?",
          "/resource/add/:domainId?",
          "/resource/:domainId/view/:id",
          "/resource/:domainId/edit/:id",
          "/resource/:domainId/auditResource/:id",
          "/resource/:domainId/validate-result",
          "/resource/:domainId/validate-resource/:id",
          "/resource/import-entity",
          "/resource/:domainId/researchQuery/:id",
        ],
        requiredPermission: { entity: "resource", action: "view" },
      },
      {
        text: "Comparison Rules",
        url: "rules-list/all",
        icon: <RuleOutlinedIcon />,
        routes: [
          "/rules-list/:domainId?",
          "/rules/:domainId/edit/:id",
          "/rules/:domainId/view/:id",
          "/rules/:domainId/rule-execution/:ruleId",
          "/rules/:domainId/auditRule/:id",
          "/rules/add",
          "/rules/import-entity",
          "/rules/:domainId/researchQuery/:id",
        ],
        requiredPermission: { entity: "rule", action: "view" },
      },
    ],
  },
  {
    text: "Connections & File Management",
    icon: <IconLinkedServicesSvg />,
    isStroke: true,
    children: [
      {
        text: "Linked Services",
        url: "/linked-services",
        icon: <LinkOutlinedIcon />,
        routes: [
          "/linked-services",
          "/linked-services/add",
          "/linked-services/view/:id",
          "/linked-services/edit/:id",
          "/linked-services/auditLinkedServices/:id",
        ],
        requiredPermission: { entity: "linked_service", action: "view" },
      },
      {
        text: "Connection Keys",
        url: "/connection-keys",
        icon: <VpnKeyOutlinedIcon />,
        routes: [
          "/connection-keys",
          "/connection-keys/add",
          "/connection-keys/view/:id",
          "/connection-keys/edit/:id",
          "/connection-keys/auditConnectionKey/:id",
        ],
        requiredPermission: { entity: "connection_key", action: "view" },
      },
      {
        text: "Blob File List",
        url: "/blob-list",
        icon: <FolderOutlinedIcon />,
        routes: ["/blob-list"],
        requiredPermission: { entity: "blob_list", action: "view" },
      },
      {
        text: "SFTP File List",
        url: "/sftp-list",
        icon: <FolderOutlinedIcon />,
        routes: ["/sftp-list"],
        requiredPermission: { entity: "sftp_list", action: "view" },
      },
    ],
  },
  {
    text: "Results",
    icon: <IconReportsWhiteSvg />,
    isStroke: true,
    children: [
      {
        text: "All Issues List",
        url: "issue-list",
        icon: <ListAltOutlinedIcon />,
        routes: ["/issue-list"],
        requiredPermission: { entity: "validation_issues", action: "view" },
      },
      {
        text: "Validation History",
        url: "validation-execution-history",
        icon: <HistoryOutlinedIcon />,
        routes: [
          "/validation-execution-history",
          "/validation-execution-history/validate-result/:resourceResultId",
        ],
        requiredPermission: { entity: "validation_result", action: "view" },
      },
      {
        text: "Validation Summary",
        url: "validation-execution-history/validation-execution-results",
        icon: <AssessmentOutlinedIcon />,
        routes: ["/validation-execution-history/validation-execution-results"],
        requiredPermission: { entity: "validation_result", action: "view" },
      },
      {
        text: "Comparison History",
        url: "rules-execution-history",
        icon: <HistoryOutlinedIcon />,
        routes: [
          "/rules-execution-history",
          "/rules-execution-history/:id/:ruleResultId/dashboard",
        ],
        requiredPermission: { entity: "rule_result", action: "view" },
      },
      {
        text: "Comparison Summary",
        url: "rules-execution-history/consolidate-results",
        icon: <AssessmentOutlinedIcon />,
        routes: ["/rules-execution-history/consolidate-results"],
        requiredPermission: { entity: "rule_result", action: "view" },
      },
      {
        text: "Generic Research Query History",
        url: "/research-execution-history",
        icon: <HistoryOutlinedIcon />,
        routes: [
          "/research-execution-history",
          "/research-execution-history/:execution_id/dashboard",
        ],
        requiredPermission: {
          entity: "generic_research_query_result",
          action: "view",
        },
      },
      {
        text: "Run Score History",
        url: "/results/run-score-history",
        icon: <AssessmentOutlinedIcon />,
        routes: ["/results/run-score-history", "/results/run-score-dashboard"],
        requiredPermission: { entity: "scoring_result", action: "view" },
      },
    ],
  },
  {
    text: "Scoring",
    icon: <IconScoringSvg />,
    isStroke: true,
    children: [
      {
        text: "All Scoring Config",
        url: "/scoring-config",
        icon: <TuneOutlinedIcon />,
        routes: [
          "/scoring-config",
          "/scoring/validation",
          "/scoring/validation/add",
          "/scoring/validation/edit",
          "/scoring/validation/view",
          "/scoring/validation/audit",
          "/scoring/comparison",
          "/scoring/comparison/add",
          "/scoring/comparison/edit",
          "/scoring/comparison/view",
          "/scoring/comparison/audit",
          "/scoring/generic-rq",
          "/scoring/generic-rq/add",
          "/scoring/generic-rq/edit",
          "/scoring/generic-rq/view",
          "/scoring/generic-rq/audit",
          "/scoring/validation-run",
          "/scoring/validation-run/add",
          "/scoring/validation-run/edit",
          "/scoring/validation-run/view",
          "/scoring/validation-run/audit",
          "/scoring/comparison-run",
          "/scoring/comparison-run/add",
          "/scoring/comparison-run/edit",
          "/scoring/comparison-run/view",
          "/scoring/research-query-run",
          "/scoring/research-query-run/add",
          "/scoring/research-query-run/edit",
          "/scoring/research-query-run/view",
          "/scoring/pipeline-weights",
          "/scoring/pipeline-weights/add",
          "/scoring/pipeline-weights/edit",
          "/scoring/pipeline-weights/view",
        ],
        requiredAnyPermission: [
          { entity: "validation_scoring_config", action: "view" },
          { entity: "comparison_scoring_config", action: "view" },
          { entity: "research_query_scoring_config", action: "view" },
          { entity: "comparison_run_scoring_config", action: "view" },
          { entity: "validation_run_scoring_config", action: "view" },
          { entity: "research_query_run_scoring_config", action: "view" },
          { entity: "run_scoring_pipeline_weight_config", action: "view" },
        ],
      },
      {
        text: "Scoring Defaults",
        url: "/scoring-defaults",
        icon: <SettingsOutlinedIcon />,
        routes: ["/scoring-defaults"],
      },
    ],
  },
  {
    text: "Run Parameters",
    icon: <IconRunInstanceSvg />,
    isStroke: true,
    children: [
      {
        text: "Execution Group",
        url: "/run-instance",
        icon: <PlayCircleOutlineIcon />,
        routes: [
          "/run-instance",
          "/run-instance/add",
          "/run-instance/edit/:id",
          "/run-instance/view/:id",
          "/scoring/run-overview/:runInstanceId",
        ],
        requiredPermission: { entity: "run_instance", action: "view" },
      },
      {
        text: "Execution Sub Group",
        url: "/execution-sub-group",
        icon: <AccountTreeOutlinedIcon />,
        routes: [
          "/execution-sub-group",
          "/execution-sub-group/add",
          "/execution-sub-group/edit/:id",
          "/execution-sub-group/view/:id",
        ],
        requiredPermission: { entity: "execution_sub_group", action: "view" },
      },
    ],
  },
  {
    text: "Research Management",
    icon: <IconResearchQueryHistorySvg />,
    isStroke: false,
    children: [
      {
        text: "Generic Research Queries",
        url: "/research-query",
        icon: <SearchOutlinedIcon />,
        routes: ["/research-query", "/research-query/add"],
        requiredPermission: {
          entity: "generic_research_query",
          action: "view",
        },
      },
      {
        text: "Comparison Research Queries",
        url: "comparison-research-queries",
        icon: <SearchOutlinedIcon />,
        routes: [
          "/comparison-research-queries",
          "/comparison-research-queries/manage",
          "/comparison-research-queries/view/:id",
          "/comparison-research-queries/auditComparison/:id",
        ],
        requiredPermission: {
          entity: "comparison_research_query",
          action: "view",
        },
      },
    ],
  },
  {
    text: "Administrative",
    icon: <IconUserSvg />,
    isStroke: false,
    children: [
      {
        text: "Dashboard",
        url: "/dashboard",
        icon: <DashboardOutlinedIcon />,
        routes: ["/dashboard"],
        // requiredPermission: { entity: "user", action: "view" },
      },
      {
        text: "Dashboard V2",
        url: "/dashboard-v2",
        icon: <DashboardOutlinedIcon />,
        routes: ["/dashboard-v2"],
        // requiredPermission: { entity: "user", action: "view" },
      },
      {
        text: "User Management",
        url: "/users",
        icon: <PeopleOutlineIcon />,
        routes: ["/users", "/users/add", "/users/edit/:id", "/users/view/:id"],
        requiredPermission: { entity: "user", action: "view" },
      },
      {
        text: "Role Management",
        url: "/roles",
        icon: <ManageAccountsOutlinedIcon />,
        routes: ["/roles", "/roles/add", "/roles/edit/:id", "/roles/view/:id"],
        requiredPermission: { entity: "user_role", action: "view" },
      },
      {
        text: "Group Management",
        url: "/groups",
        icon: <GroupsOutlinedIcon />,
        routes: [
          "/groups",
          "/groups/add",
          "/groups/edit/:id",
          "/groups/view/:id",
        ],
        requiredPermission: { entity: "user_group", action: "view" },
      },
      {
        text: "Tag Management",
        url: "/tags",
        icon: <LocalOfferOutlinedIcon />,
        routes: ["/tags", "/tags/add", "/tags/edit/:id", "/tags/view/:id"],
        requiredPermission: { entity: "auth_tag", action: "view" },
      },
      {
        text: "System Management",
        url: "/system",
        icon: <SettingsOutlinedIcon />,
        routes: [
          "/system",
          "/system/add",
          "/system/edit/:id",
          "/system/view/:id",
        ],
        requiredPermission: { entity: "system", action: "view" },
      },
    ],
  },
];

// Helper function to find a child nav item by its exact URL
export const findChildByUrl = (url: string): NavSubItem | null => {
  for (const navItem of navItems) {
    if (navItem.children) {
      for (const child of navItem.children) {
        const normalizedChildUrl = child.url.startsWith("/")
          ? child.url
          : `/${child.url}`;
        if (normalizedChildUrl === url) return child;
      }
    }
  }
  return null;
};

// Helper function to find permission requirement for a given URL
export const findRouteRequirement = (
  url: string,
): { entity: string; action: string } | null => {
  for (const navItem of navItems) {
    // Check if this nav item has children
    if (navItem.children) {
      for (const child of navItem.children) {
        if (child.routes) {
          for (const route of child.routes) {
            const regex = new RegExp(
              "^" +
                route.replace(/:[^/]+/g, "[^/]+").replace(/\//g, "\\/") +
                "$",
            );
            if (regex.test(url) && child.requiredPermission) {
              return child.requiredPermission;
            }
          }
        }
      }
    }
    // Check direct nav item routes
    if (navItem.routes) {
      for (const route of navItem.routes) {
        const regex = new RegExp(
          "^" + route.replace(/:[^/]+/g, "[^/]+").replace(/\//g, "\\/") + "$",
        );
        if (regex.test(url) && navItem.requiredPermission) {
          return navItem.requiredPermission;
        }
      }
    }
  }
  return null;
};

export const doesRouteMatch = (pathname: string, route: string): boolean => {
  if (route === "/") return pathname === "/";

  const regex = new RegExp(
    "^" + route.replace(/:[^/]+/g, "[^/]+").replace(/\//g, "\\/") + "$",
  );
  return regex.test(pathname);
};

export const findActiveParent = (pathname: string): string | null =>
  navItems.find((navItem) => {
    const routes = navItem.children
      ? navItem.children.flatMap((child) => child.routes || [])
      : navItem.routes || [];
    return routes.some((route) => doesRouteMatch(pathname, route));
  })?.text || null;
